import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, Briefcase } from 'lucide-react';

const JobSearchSelect = ({ 
  jobs, 
  selectedJobId, 
  onSelect, 
  placeholder = "Search for a job...",
  disabled = false,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selectedJob = jobs.find(j => j.id === selectedJobId);

  // Use useMemo instead of useEffect + setState to avoid cascading renders
  const filteredJobs = useMemo(() => {
    if (searchTerm.trim() === '') {
      return jobs.slice(0, 10); // Show first 10 by default
    }
    const term = searchTerm.toLowerCase();
    const filtered = jobs.filter(job => 
      job.title?.toLowerCase().includes(term) ||
      job.job_code?.toLowerCase().includes(term) ||
      job.location?.toLowerCase().includes(term) ||
      job.client_name?.toLowerCase().includes(term)
    );
    return filtered.slice(0, 20); // Limit to 20 results
  }, [searchTerm, jobs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (job) => {
    onSelect(job.id);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect('');
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Selected Job Display or Search Input */}
      {selectedJob && !isOpen ? (
        <div 
          className={`flex items-center justify-between w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-white cursor-pointer hover:border-slate-300 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          onClick={() => !disabled && setIsOpen(true)}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-slate-900 truncate block">
                {selectedJob.job_code && <span className="font-mono text-blue-600 mr-2">{selectedJob.job_code}</span>}
                {selectedJob.title}
              </span>
              {selectedJob.location && (
                <span className="text-xs text-slate-500 truncate block">{selectedJob.location}</span>
              )}
            </div>
          </div>
          {!disabled && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-slate-100 rounded ml-2 flex-shrink-0"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${disabled ? 'bg-slate-50 opacity-60 cursor-not-allowed' : ''}`}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filteredJobs.length > 0 ? (
            <ul className="py-1">
              {filteredJobs.map((job) => (
                <li
                  key={job.id}
                  onClick={() => handleSelect(job)}
                  className={`px-4 py-2.5 cursor-pointer hover:bg-slate-50 ${job.id === selectedJobId ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {job.job_code && (
                          <span className="font-mono text-xs bg-slate-100 text-blue-600 px-1.5 py-0.5 rounded">
                            {job.job_code}
                          </span>
                        )}
                        <span className="text-slate-900 truncate">{job.title}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                        {job.location && <span className="truncate">{job.location}</span>}
                        {job.client_name && (
                          <>
                            <span>•</span>
                            <span className="truncate">{job.client_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      job.status === 'completed' ? 'bg-green-100 text-green-700' :
                      job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {job.status?.replace('_', ' ')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center text-slate-500">
              <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No jobs found</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobSearchSelect;
